import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

// ── Local types ────────────────────────────────────────────────────────────────

interface VendorRiskProfile {
  id: string;
  vendor_name: string;
  vendor_domain: string;
  vendor_category: string;
  risk_tier: 'critical' | 'high' | 'medium' | 'low';
  data_types: string[];
  discovery_source: 'shadow_ai' | 'casb' | 'manual';
  is_monitored: boolean;
  last_checked_at?: string;
  breach_count: number;
  active_alerts: number;
  hibp_breaches: string[];
}

interface VendorBreachAlert {
  id: string;
  vendor_id: string;
  vendor_name: string;
  vendor_domain: string;
  breach_name: string;
  breach_date?: string;
  breach_description?: string;
  exposed_data_types: string[];
  affected_client_data: string[];
  severity: 'critical' | 'high' | 'medium';
  status: 'new' | 'investigating' | 'mitigated' | 'false_positive';
  recommended_actions: string[];
  created_at: string;
}

interface SummaryData {
  data: {
    vendors_monitored: number;
    total_vendors: number;
    active_alerts: number;
    critical_vendors: number;
    data_types_at_risk: string[];
  };
}

interface VendorsResponse {
  data: VendorRiskProfile[];
}

interface AlertsResponse {
  data: VendorBreachAlert[];
}

// Well-known vendor catalog for auto-detect (client-side mirror of API catalog)
const KNOWN_VENDORS_CLIENT: Record<string, { category: string; risk_tier: 'critical' | 'high' | 'medium' | 'low'; data_types: string[] }> = {
  'salesforce.com': { category: 'crm', risk_tier: 'critical', data_types: ['customer_pii', 'financial', 'employee'] },
  'hubspot.com': { category: 'crm', risk_tier: 'high', data_types: ['customer_pii'] },
  'slack.com': { category: 'communication', risk_tier: 'high', data_types: ['employee', 'ip'] },
  'microsoft.com': { category: 'productivity', risk_tier: 'critical', data_types: ['employee', 'customer_pii', 'financial', 'ip'] },
  'google.com': { category: 'productivity', risk_tier: 'critical', data_types: ['employee', 'customer_pii', 'ip'] },
  'dropbox.com': { category: 'storage', risk_tier: 'high', data_types: ['employee', 'customer_pii', 'ip'] },
  'box.com': { category: 'storage', risk_tier: 'high', data_types: ['employee', 'customer_pii', 'ip'] },
  'zoom.us': { category: 'communication', risk_tier: 'medium', data_types: ['employee'] },
  'github.com': { category: 'devtools', risk_tier: 'critical', data_types: ['ip'] },
  'gitlab.com': { category: 'devtools', risk_tier: 'critical', data_types: ['ip'] },
  'stripe.com': { category: 'finance', risk_tier: 'critical', data_types: ['financial', 'customer_pii'] },
  'zendesk.com': { category: 'crm', risk_tier: 'high', data_types: ['customer_pii'] },
  'atlassian.com': { category: 'devtools', risk_tier: 'high', data_types: ['employee', 'ip'] },
  'docusign.com': { category: 'finance', risk_tier: 'critical', data_types: ['customer_pii', 'financial'] },
  'openai.com': { category: 'ai', risk_tier: 'high', data_types: ['ip', 'customer_pii'] },
  'anthropic.com': { category: 'ai', risk_tier: 'high', data_types: ['ip'] },
  'workday.com': { category: 'hr', risk_tier: 'critical', data_types: ['employee', 'financial'] },
};

const ALL_DATA_TYPES = ['customer_pii', 'financial', 'employee', 'ip', 'health'] as const;
const DATA_TYPE_LABELS: Record<string, string> = {
  customer_pii: 'Customer PII',
  financial: 'Financial',
  employee: 'Employee',
  ip: 'Intellectual Property',
  health: 'Health / PHI',
};

const CATEGORIES = ['crm', 'hr', 'finance', 'devtools', 'communication', 'storage', 'security', 'productivity', 'infrastructure', 'ai', 'other'];
const RISK_TIERS: Array<'critical' | 'high' | 'medium' | 'low'> = ['critical', 'high', 'medium', 'low'];

// Sort vendors by risk tier (critical first)
const riskOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

// ── Badge helpers ──────────────────────────────────────────────────────────────

function riskBadge(tier: string) {
  const colors =
    tier === 'critical' ? 'bg-red-100 text-red-700' :
    tier === 'high' ? 'bg-orange-100 text-orange-700' :
    tier === 'medium' ? 'bg-yellow-100 text-yellow-700' :
    'bg-blue-100 text-blue-700';
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors}`}>
      {tier}
    </span>
  );
}

function severityBadge(severity: string) {
  const colors =
    severity === 'critical' ? 'bg-red-100 text-red-700' :
    severity === 'high' ? 'bg-orange-100 text-orange-700' :
    'bg-yellow-100 text-yellow-700';
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors}`}>
      {severity}
    </span>
  );
}

function statusBadge(status: string) {
  const colors =
    status === 'new' ? 'bg-red-100 text-red-700' :
    status === 'investigating' ? 'bg-yellow-100 text-yellow-700' :
    status === 'mitigated' ? 'bg-emerald-100 text-emerald-700' :
    'bg-gray-100 text-gray-500';
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${colors}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function categoryBadge(category: string) {
  return (
    <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
      {category}
    </span>
  );
}

function sourceLabel(source: string) {
  const map: Record<string, string> = { shadow_ai: 'Shadow AI', casb: 'CASB', manual: 'Manual' };
  return map[source] ?? source;
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function SupplyChainRiskPage() {
  const { data: summaryData, refresh: refreshSummary } = useApi<SummaryData>('/protect/supply-chain?action=summary');
  const { data: vendorsData, loading, error, refresh: refreshVendors } = useApi<VendorsResponse>('/protect/supply-chain');
  const { data: alertsData, refresh: refreshAlerts } = useApi<AlertsResponse>('/protect/supply-chain?action=alerts');

  const [activeTab, setActiveTab] = useState<'vendors' | 'alerts'>('vendors');
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);

  // Add vendor form state
  const [formName, setFormName] = useState('');
  const [formDomain, setFormDomain] = useState('');
  const [formCategory, setFormCategory] = useState('other');
  const [formRiskTier, setFormRiskTier] = useState<'critical' | 'high' | 'medium' | 'low'>('medium');
  const [formDataTypes, setFormDataTypes] = useState<string[]>([]);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const summary = summaryData?.data;
  const vendors = (vendorsData?.data ?? []).slice().sort((a, b) => (riskOrder[a.risk_tier] ?? 3) - (riskOrder[b.risk_tier] ?? 3));
  const alerts = alertsData?.data ?? [];
  const activeAlerts = alerts.filter(a => a.status === 'new' || a.status === 'investigating');

  function refresh() {
    refreshSummary();
    refreshVendors();
    refreshAlerts();
  }

  // Auto-detect known vendor when domain is typed
  function handleDomainChange(domain: string) {
    setFormDomain(domain);
    const known = KNOWN_VENDORS_CLIENT[domain.toLowerCase()];
    if (known) {
      setFormCategory(known.category);
      setFormRiskTier(known.risk_tier);
      setFormDataTypes(known.data_types);
    }
  }

  function toggleDataType(dt: string) {
    setFormDataTypes(prev =>
      prev.includes(dt) ? prev.filter(x => x !== dt) : [...prev, dt]
    );
  }

  async function handleAddVendor(e: React.FormEvent) {
    e.preventDefault();
    if (!formName.trim() || !formDomain.trim()) return;
    setFormSubmitting(true);
    try {
      await post('/protect/supply-chain?action=add_vendor', {
        vendor_name: formName.trim(),
        vendor_domain: formDomain.trim().toLowerCase(),
        vendor_category: formCategory,
        risk_tier: formRiskTier,
        data_types: formDataTypes,
      });
      setShowAddModal(false);
      setFormName('');
      setFormDomain('');
      setFormCategory('other');
      setFormRiskTier('medium');
      setFormDataTypes([]);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add vendor');
    } finally {
      setFormSubmitting(false);
    }
  }

  async function handleSyncShadowAI() {
    setActionLoading('sync-shadow-ai');
    try {
      await post('/protect/supply-chain?action=sync_from_shadow_ai');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCheckNow(vendorId: string) {
    setActionLoading(`check-${vendorId}`);
    try {
      await post('/protect/supply-chain?action=check_vendor', { vendor_id: vendorId });
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleUpdateAlertStatus(alertId: string, status: VendorBreachAlert['status']) {
    setActionLoading(`alert-${alertId}`);
    try {
      await post(`/protect/supply-chain/alerts/${alertId}?action=update_status`, { status });
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading supply chain risk data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Supply Chain Risk Monitor</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Detect vendor security breaches before they impact your organization.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSyncShadowAI}
            disabled={actionLoading === 'sync-shadow-ai'}
            className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {actionLoading === 'sync-shadow-ai' ? 'Syncing...' : 'Sync from Shadow AI'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
          >
            + Add Vendor
          </button>
        </div>
      </div>

      {/* Stats row */}
      {summary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Vendors Monitored</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.vendors_monitored}</p>
            <p className="text-xs text-gray-400 mt-0.5">{summary.total_vendors} total</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Active Breach Alerts</p>
            <p className={`text-2xl font-bold mt-1 ${summary.active_alerts > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {summary.active_alerts}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Critical Vendors</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{summary.critical_vendors}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Data Types at Risk</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {summary.data_types_at_risk.length === 0 ? (
                <p className="text-gray-400 text-xs">None</p>
              ) : (
                summary.data_types_at_risk.slice(0, 3).map(dt => (
                  <span key={dt} className="px-1.5 py-0.5 text-xs bg-red-50 text-red-600 rounded">
                    {DATA_TYPE_LABELS[dt] ?? dt}
                  </span>
                ))
              )}
              {summary.data_types_at_risk.length > 3 && (
                <span className="text-xs text-gray-400">+{summary.data_types_at_risk.length - 3} more</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alert banner */}
      {activeAlerts.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <span className="text-red-500 text-lg">⚠</span>
          <p className="text-red-700 font-medium">
            {activeAlerts.length} vendor breach alert{activeAlerts.length === 1 ? '' : 's'} require{activeAlerts.length === 1 ? 's' : ''} attention
          </p>
          <button
            onClick={() => setActiveTab('alerts')}
            className="ml-auto text-sm text-red-600 underline font-medium"
          >
            View Alerts
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {(['vendors', 'alerts'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'alerts' ? `Breach Alerts${activeAlerts.length > 0 ? ` (${activeAlerts.length})` : ''}` : 'Vendors'}
            </button>
          ))}
        </nav>
      </div>

      {/* Vendors tab */}
      {activeTab === 'vendors' && (
        <div>
          {vendors.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium mb-2">No vendors tracked yet</p>
              <p className="text-sm">Add vendors manually or sync from Shadow AI / CASB connections.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Risk Tier</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Data Types</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Breaches</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Last Checked</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vendors.map(vendor => (
                    <tr key={vendor.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{vendor.vendor_name}</div>
                        <div className="text-xs text-gray-400">{vendor.vendor_domain}</div>
                        {vendor.active_alerts > 0 && (
                          <span className="inline-block mt-0.5 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                            {vendor.active_alerts} alert{vendor.active_alerts === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{categoryBadge(vendor.vendor_category)}</td>
                      <td className="px-4 py-3">{riskBadge(vendor.risk_tier)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {vendor.data_types.map(dt => (
                            <span key={dt} className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              {DATA_TYPE_LABELS[dt] ?? dt}
                            </span>
                          ))}
                          {vendor.data_types.length === 0 && <span className="text-gray-400 text-xs">None specified</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${vendor.breach_count > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {vendor.breach_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {vendor.last_checked_at
                          ? new Date(vendor.last_checked_at).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-400">{sourceLabel(vendor.discovery_source)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleCheckNow(vendor.id)}
                          disabled={actionLoading === `check-${vendor.id}`}
                          className="px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
                        >
                          {actionLoading === `check-${vendor.id}` ? 'Checking...' : 'Check Now'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Breach Alerts tab */}
      {activeTab === 'alerts' && (
        <div>
          {alerts.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium mb-2">No breach alerts</p>
              <p className="text-sm">Alerts appear here when monitored vendors are found in HIBP breach data.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map(alert => (
                <div key={alert.id} className={`bg-white rounded-lg border p-4 ${alert.status === 'new' ? 'border-red-200' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{alert.vendor_name}</span>
                        <span className="text-gray-400 text-sm">—</span>
                        <span className="font-medium text-gray-700">{alert.breach_name}</span>
                        {severityBadge(alert.severity)}
                        {statusBadge(alert.status)}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span>{alert.vendor_domain}</span>
                        {alert.breach_date && <span>Breach date: {alert.breach_date}</span>}
                        <span>Detected: {new Date(alert.created_at).toLocaleDateString()}</span>
                      </div>

                      {alert.affected_client_data.length > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-red-600 font-medium">Your data at risk:</span>
                          {alert.affected_client_data.map(dt => (
                            <span key={dt} className="px-1.5 py-0.5 text-xs bg-red-50 text-red-600 rounded">
                              {DATA_TYPE_LABELS[dt] ?? dt}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Expandable recommended actions */}
                      <button
                        onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
                        className="mt-2 text-xs text-emerald-600 underline"
                      >
                        {expandedAlert === alert.id ? 'Hide' : 'Show'} recommended actions ({alert.recommended_actions.length})
                      </button>

                      {expandedAlert === alert.id && (
                        <ul className="mt-2 space-y-1">
                          {alert.recommended_actions.map((action, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                              <span className="text-emerald-500 mt-0.5">•</span>
                              {action}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Status actions */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {alert.status === 'new' && (
                        <button
                          onClick={() => handleUpdateAlertStatus(alert.id, 'investigating')}
                          disabled={actionLoading === `alert-${alert.id}`}
                          className="px-3 py-1.5 text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 rounded hover:bg-yellow-100 disabled:opacity-50"
                        >
                          Investigate
                        </button>
                      )}
                      {(alert.status === 'new' || alert.status === 'investigating') && (
                        <button
                          onClick={() => handleUpdateAlertStatus(alert.id, 'mitigated')}
                          disabled={actionLoading === `alert-${alert.id}`}
                          className="px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
                        >
                          Mark Mitigated
                        </button>
                      )}
                      {alert.status === 'new' && (
                        <button
                          onClick={() => handleUpdateAlertStatus(alert.id, 'false_positive')}
                          disabled={actionLoading === `alert-${alert.id}`}
                          className="px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          False Positive
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Vendor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Vendor to Monitor</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            <form onSubmit={handleAddVendor} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. Salesforce"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain
                  <span className="ml-1 text-xs text-gray-400">(auto-detects known vendors)</span>
                </label>
                <input
                  type="text"
                  value={formDomain}
                  onChange={e => handleDomainChange(e.target.value)}
                  placeholder="e.g. salesforce.com"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                {KNOWN_VENDORS_CLIENT[formDomain.toLowerCase()] && (
                  <p className="mt-1 text-xs text-emerald-600">Known vendor detected — fields pre-filled.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formCategory}
                    onChange={e => setFormCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Tier</label>
                  <select
                    value={formRiskTier}
                    onChange={e => setFormRiskTier(e.target.value as typeof formRiskTier)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {RISK_TIERS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Types This Vendor Holds</label>
                <div className="space-y-2">
                  {ALL_DATA_TYPES.map(dt => (
                    <label key={dt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formDataTypes.includes(dt)}
                        onChange={() => toggleDataType(dt)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-gray-700">{DATA_TYPE_LABELS[dt]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {formSubmitting ? 'Adding...' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
